// The assistant's conversation and agent loop.
//
// The server (functions/agent.js) returns Claude's next message. When it asks
// for tools we run them here, one at a time, against the live app, and send
// the results back until Claude is done. Every turn keeps a snapshot of the
// user's lists so the whole turn can be undone.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '../firebase';
import { useApp } from '../context/AppContext';
import { usePomodoro } from '../context/PomodoroContext';
import { FUNCTION_URLS } from '../config/functions';
import { confirmationFor, localDateString, OUTSIDE_UNDO, runTool } from './tools';
import { withAction } from './actionLog';
import { hideNext, loadChats, saveChats, splitNext, yesterdayOf } from './chatStore';

const AgentContext = createContext(null);

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) throw new Error('useAgent must be used within an AgentProvider');
  return context;
};

// Safety net against a runaway loop; a normal turn uses a handful of steps
const MAX_STEPS_PER_TURN = 16;
// How long to wait for React to commit a write before moving on anyway
const SETTLE_TIMEOUT_MS = 400;

const UNDOABLE_KEYS = ['habits', 'completions', 'tasks', 'archivedTasks', 'goals', 'archivedGoals'];

export const clockLine = () => {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  return `[${weekday} ${localDateString(now)} ${time} ${zone}]`;
};

export const authHeaders = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in again to use the assistant.');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

export const failed = (data) => {
  const error = new Error(data.error || 'The assistant is unavailable right now.');
  error.code = data.code;
  return error;
};

// One step of the loop. The reply streams back as newline-delimited JSON:
// text as Claude writes it (passed to onText), then the whole message.
const callAgent = async (messages, onText) => {
  const url = FUNCTION_URLS.agent.chat;
  if (!url) throw new Error('The assistant is not configured for this build.');
  const response = await fetch(url, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ messages, stream: true }),
  });
  const type = response.headers.get('Content-Type') || '';
  if (!response.ok || !type.includes('ndjson') || !response.body) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw failed(data);
    return data;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  const handle = (line) => {
    if (!line.trim()) return null;
    const event = JSON.parse(line);
    if (event.type === 'text') onText(event.text);
    else if (event.type === 'error') throw failed(event);
    else if (event.type === 'done') return { content: event.content, stop_reason: event.stop_reason };
    return null;
  };
  for (;;) {
    const { value, done } = await reader.read();
    buffered += decoder.decode(value || new Uint8Array(), { stream: !done });
    const lines = buffered.split('\n');
    buffered = done ? '' : lines.pop();
    for (const line of lines) {
      const result = handle(line);
      if (result) return result;
    }
    if (done) break;
  }
  throw new Error('The reply was cut off. Try again.');
};

// Quick add: turn a typed line into task or habit fields
export const parseQuickAdd = async (kind, text) => {
  const url = FUNCTION_URLS.agent.parse;
  if (!url) throw new Error('The assistant is not configured for this build.');
  const response = await fetch(url, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ kind, text, clock: clockLine() }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw failed(data);
  return data.fields;
};

// Restore a snapshot. Firestore merges nested maps, so a completion added
// during the turn has to be written back as false rather than just left out.
const restoreSnapshot = (snapshot, current) => {
  const completions = JSON.parse(JSON.stringify(snapshot.completions || {}));
  Object.entries(current.completions || {}).forEach(([habitId, days]) => {
    Object.entries(days || {}).forEach(([day, value]) => {
      if (value && !completions[habitId]?.[day]) {
        completions[habitId] = { ...(completions[habitId] || {}), [day]: false };
      }
    });
  });
  return { ...snapshot, completions };
};

const equalState = (a, b) => JSON.stringify(a) === JSON.stringify(b);

let nextId = 0;
const newId = () => `a${Date.now()}_${nextId++}`;

export const AgentProvider = ({ children }) => {
  const app = useApp();
  const pomodoro = usePomodoro();

  // Today's conversation and yesterday's (the archive), kept in this browser
  // (chatStore.js). The provider is keyed by user, so this runs per sign-in
  const uid = app.user?.userId;
  const [stored] = useState(() => (uid ? loadChats(uid) : { current: null, archive: null }));

  // What the panel shows: { id, kind: 'user'|'assistant'|'action'|'error', text, ... }
  const [entries, setEntries] = useState(() => stored.current?.entries || []);
  const [busy, setBusy] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [undoable, setUndoable] = useState(null); // { snapshot, partial }
  const [archive, setArchive] = useState(stored.archive);

  // The conversation as the API sees it, and the day it belongs to
  const messagesRef = useRef(stored.current?.messages || []);
  const dayRef = useRef(localDateString());
  // Something the assistant should know, sent with the next message (e.g. an undo)
  const noteRef = useRef(null);
  const pendingRef = useRef(null);

  // Latest app and timer, and a way to wait for the next render after a write
  const appRef = useRef(app);
  const pomodoroRef = useRef(pomodoro);
  appRef.current = app;
  pomodoroRef.current = pomodoro;
  const renderWaiters = useRef([]);
  useEffect(() => {
    const waiters = renderWaiters.current;
    renderWaiters.current = [];
    waiters.forEach((resolve) => resolve());
  });

  const settle = useCallback(() => {
    const before = appRef.current;
    return new Promise((resolve) => {
      const done = () => { clearTimeout(timer); resolve(); };
      const timer = setTimeout(done, SETTLE_TIMEOUT_MS);
      renderWaiters.current.push(() => {
        if (appRef.current !== before) done();
      });
      // A write that already rendered doesn't need to wait
      if (appRef.current !== before) done();
    });
  }, []);

  const toolContext = useRef({
    get: () => appRef.current,
    pomodoro: () => pomodoroRef.current,
    settle,
  }).current;

  const addEntry = useCallback((entry) => {
    const withId = { id: newId(), ...entry };
    setEntries((list) => [...list, withId]);
    return withId.id;
  }, []);

  // What a tool did, folded into the line before when it continues a run
  const addAction = useCallback((outcome) => {
    setEntries((list) => withAction(list, outcome, newId()));
  }, []);

  const updateEntry = useCallback((id, changes) => {
    setEntries((list) => list.map((e) => (e.id === id ? { ...e, ...changes } : e)));
  }, []);

  const removeEntry = useCallback((id) => {
    setEntries((list) => list.filter((e) => e.id !== id));
  }, []);

  const askToConfirm = useCallback((request) => new Promise((resolve) => {
    pendingRef.current = { ...request, resolve };
    setPendingConfirm(pendingRef.current);
  }), []);

  const answerConfirm = useCallback((approved) => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    setPendingConfirm(null);
    if (!approved) addEntry({ kind: 'action', text: 'Skipped', muted: true });
    pending.resolve(approved);
  }, [addEntry]);

  const send = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    setBusy(true);
    setUndoable(null);
    addEntry({ kind: 'user', text: trimmed, time: new Date() });

    const turnStart = messagesRef.current.length;
    const snapshot = Object.fromEntries(UNDOABLE_KEYS.map((k) => [k, appRef.current[k]]));
    let changedData = false;
    let changedOutsideUndo = false;
    let answered = false;
    // The reply being streamed in, if any, so a failed request can take it back out
    let activeStreamId = null;

    const note = noteRef.current ? `${noteRef.current}\n\n` : '';
    noteRef.current = null;
    messagesRef.current = [...messagesRef.current, {
      role: 'user',
      content: [{ type: 'text', text: `${note}${clockLine()} ${trimmed}` }],
    }];

    try {
      for (let step = 0; step < MAX_STEPS_PER_TURN; step++) {
        // Show the reply as it's written; the finished message replaces it below
        let streamId = null;
        activeStreamId = null;
        let streamed = '';
        const response = await callAgent(messagesRef.current, (delta) => {
          streamed += delta;
          const text = hideNext(streamed.trimStart());
          if (!text) return;
          if (streamId) updateEntry(streamId, { text });
          else activeStreamId = streamId = addEntry({ kind: 'assistant', text, streaming: true });
        });

        if (response.stop_reason === 'refusal') {
          if (streamId) removeEntry(streamId);
          // A refused turn can't be continued, so drop it from the conversation
          messagesRef.current = messagesRef.current.slice(0, turnStart);
          addEntry({ kind: 'error', text: "The assistant can't help with that one." });
          break;
        }

        let content = Array.isArray(response.content) ? response.content : [];
        answered = true;
        // A tool call cut off by max_tokens can't be answered; keep the rest
        if (response.stop_reason !== 'tool_use') content = content.filter((b) => b.type !== 'tool_use');
        if (content.length) messagesRef.current = [...messagesRef.current, { role: 'assistant', content }];

        const { text: reply, next } = splitNext(content.filter((b) => b.type === 'text').map((b) => b.text).join('\n\n'));
        if (streamId && reply) updateEntry(streamId, { text: reply, next, streaming: false });
        else if (streamId) removeEntry(streamId);
        else if (reply) addEntry({ kind: 'assistant', text: reply, next });
        activeStreamId = null;

        const toolUses = content.filter((b) => b.type === 'tool_use');
        if (response.stop_reason !== 'tool_use' || toolUses.length === 0) {
          if (response.stop_reason === 'max_tokens') {
            addEntry({ kind: 'error', text: 'That reply was cut short. Ask it to continue.' });
          }
          break;
        }

        // Run in order: each write must see the one before it
        const results = [];
        for (const block of toolUses) {
          const confirm = confirmationFor(block.name, block.input, appRef.current);
          if (confirm) {
            const approved = await askToConfirm({ ...confirm, tool: block.name });
            if (!approved) {
              results.push({ type: 'tool_result', tool_use_id: block.id, content: 'The user declined.', is_error: true });
              continue;
            }
          }
          const outcome = await runTool(block.name, block.input, toolContext);
          if (!outcome.isError && outcome.summary) {
            addAction(outcome);
            if (OUTSIDE_UNDO.has(block.name)) changedOutsideUndo = true;
            else changedData = true;
          }
          results.push({
            type: 'tool_result',
            tool_use_id: block.id,
            content: outcome.content,
            ...(outcome.isError ? { is_error: true } : {}),
          });
        }
        // The conversation now ends on the tool results; if the loop stops
        // here, the next message joins that user turn and carries on from it
        messagesRef.current = [...messagesRef.current, { role: 'user', content: results }];

        if (step === MAX_STEPS_PER_TURN - 1) {
          addEntry({ kind: 'error', text: 'That took too many steps, so it stopped. Ask again to carry on.' });
        }
      }
    } catch (error) {
      // A reply cut off mid-stream isn't part of the conversation; don't leave half of it showing
      if (activeStreamId) removeEntry(activeStreamId);
      // Nothing came back this turn, so forget the message; it can be sent again
      if (!answered) messagesRef.current = messagesRef.current.slice(0, turnStart);
      addEntry({ kind: 'error', text: error.message, code: error.code });
    } finally {
      if (pendingRef.current) answerConfirm(false);
      if (changedData) {
        setUndoable({
          snapshot,
          partial: changedOutsideUndo,
          latest: Object.fromEntries(UNDOABLE_KEYS.map((k) => [k, appRef.current[k]])),
        });
      }
      setBusy(false);
    }
  }, [busy, addEntry, addAction, updateEntry, removeEntry, askToConfirm, answerConfirm, toolContext]);

  const undoTurn = useCallback(async () => {
    if (!undoable) return;
    const current = appRef.current;
    if (undoable.latest && UNDOABLE_KEYS.some((k) => !equalState(current[k], undoable.latest[k]))) {
      setUndoable(null);
      addEntry({
        kind: 'error',
        text: "Can't undo safely because those lists changed since the assistant reply.",
      });
      return;
    }
    await current.updateUserData(restoreSnapshot(undoable.snapshot, current));
    setUndoable(null);
    addEntry({
      kind: 'action',
      text: undoable.partial ? 'Undid changes to habits, tasks and goals. Calendar and settings changes stay.' : 'Undid that',
    });
    noteRef.current = '(I pressed undo: the changes you made to my habits, tasks and goals in your last reply have been reverted.)';
  }, [undoable, addEntry]);

  const reset = useCallback(() => {
    if (busy) return;
    messagesRef.current = [];
    noteRef.current = null;
    setEntries([]);
    setUndoable(null);
  }, [busy]);

  // Keep the conversation between turns (never mid-turn, when the messages
  // may end in a tool call still waiting for its result)
  useEffect(() => {
    if (!uid || busy) return;
    saveChats(uid, { current: { day: dayRef.current, entries, messages: messagesRef.current }, archive });
  }, [uid, busy, entries, archive]);

  // A new day: today's conversation becomes the archive and a fresh one
  // starts. Checked whenever the app comes back to the screen
  useEffect(() => {
    const roll = () => {
      const today = localDateString();
      if (document.visibilityState === 'hidden' || dayRef.current === today || busy) return;
      // Kept for one day: yesterday's chat is archived; an older one (the app
      // left open for days) and the archive before it are dropped
      const fromYesterday = dayRef.current === yesterdayOf(today);
      setArchive(entries.length && fromYesterday ? { day: dayRef.current, entries, messages: messagesRef.current } : null);
      dayRef.current = today;
      messagesRef.current = [];
      noteRef.current = null;
      setEntries([]);
      setUndoable(null);
    };
    document.addEventListener('visibilitychange', roll);
    const id = setInterval(roll, 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', roll);
      clearInterval(id);
    };
  }, [busy, entries]);

  // Pick yesterday's conversation back up: it becomes today's, and leaves
  // the archive. Only from an empty chat, so nothing of today's is lost
  const continueArchive = useCallback(() => {
    if (busy || !archive || messagesRef.current.length) return;
    messagesRef.current = archive.messages;
    noteRef.current = null;
    setEntries(archive.entries);
    setArchive(null);
  }, [busy, archive]);

  // What the last reply suggested asking next, for the text box
  const last = entries[entries.length - 1];
  const nextPrompt = !busy && last?.kind === 'assistant' ? last.next || null : null;

  const value = {
    entries,
    busy,
    send,
    reset,
    pendingConfirm,
    answerConfirm,
    canUndo: !!undoable && !busy,
    undoTurn,
    nextPrompt,
    archive: archive && { day: archive.day, entries: archive.entries },
    continueArchive,
  };

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};
